import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchPlayerTeams, fetchPlayerInvitations, respondToInvitation, searchPlayers } from '../../api/TeamsApi';
import { API_BASE_URL } from '../../api/Constants';

const PlayerTeams = () => {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showInvitations, setShowInvitations] = useState(false);
  const [teamInvitations, setTeamInvitations] = useState([]);
  const [invitationsLoading, setInvitationsLoading] = useState(false);
  
  // Add these new state variables for player search
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [inviteSuccess, setInviteSuccess] = useState(null);
  const [inviteError, setInviteError] = useState(null);
  
  // Add these state variables for modals
  const [selectedTeamForDetails, setSelectedTeamForDetails] = useState(null);
  const [selectedTeamForManage, setSelectedTeamForManage] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [removeConfirmation, setRemoveConfirmation] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null); // State for delete confirmation modal
  
  // Add these new state variables for error handling
  const [memberRemovalError, setMemberRemovalError] = useState(null);
  
  // Fetch teams on component mount
  useEffect(() => {
    const loadTeams = async () => {
      try {
        setLoading(true);
        const teamsData = await fetchPlayerTeams();
        
        // Process the teams data to match our component's expected format
        const processedTeams = teamsData.teams.map(team => ({
          id: team.id,
          name: team.name,
          bio: team.bio,
          // Use the team_type from API instead of default value
          game: getGameDisplay(team.team_type || "default"),
          members: team.members_details.length,
          // Determine if the current user is the team owner
          role: isTeamOwner(team) ? "Leader" : "Member"
        }));
        
        setTeams(processedTeams);
        setError(null);
      } catch (err) {
        console.error("Failed to load teams:", err);
        setError("Failed to load your teams. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    
    loadTeams();
  }, []);
  
  // Helper function to convert team_type to display name
  const getGameDisplay = (gameType) => {
    const gameMap = {
      "freefire": "Free Fire",
      "pubg": "PUBG Mobile",
      "valorant": "Valorant",
      "cod": "Call of Duty",
      "bgmi": "BGMI",
      "default": "Other Game"
    };
    
    return gameMap[gameType] || gameType;
  };
  
  // Function to check if current user is team owner
  const isTeamOwner = (team) => {
    const userInfo = JSON.parse(localStorage.getItem("user_info") || "{}");
    const userEmail = userInfo.email;
    return team.team_owner_email === userEmail;
  };
  
  // Fetch invitations when needed
  const loadInvitations = async () => {
    try {
      setInvitationsLoading(true);
      const data = await fetchPlayerInvitations();
      
      // Format invitations for our component
      if (data.invitations && Array.isArray(data.invitations)) {
        const formattedInvitations = data.invitations.map(inv => ({
          id: inv.team_id,
          name: inv.team_name,
          game: "PUBG Mobile", // Default game
          owner: inv.owner,
          members: inv.total_members,
          invitedAt: new Date().toISOString() // Default to current date if not provided
        }));
        
        setTeamInvitations(formattedInvitations);
      } else {
        setTeamInvitations([]);
      }
    } catch (err) {
      console.error("Failed to load invitations:", err);
    } finally {
      setInvitationsLoading(false);
    }
  };
  
  // Load invitations when showInvitations is toggled to true
  useEffect(() => {
    if (showInvitations) {
      loadInvitations();
    }
  }, [showInvitations]);
  
  // Handle invitation response
  const handleInvitationResponse = async (teamId, response) => {
    try {
      await respondToInvitation(teamId, response);
      // Remove the invitation from the list
      setTeamInvitations(prevInvitations => 
        prevInvitations.filter(invitation => invitation.id !== teamId)
      );
      
      // If user accepted, refresh the teams list
      if (response === 'accept') {
        const teamsData = await fetchPlayerTeams();
        const processedTeams = teamsData.teams.map(team => ({
          id: team.id,
          name: team.name,
          bio: team.bio,
          game: "PUBG Mobile",
          members: team.members_details.length,
          role: isTeamOwner(team) ? "Leader" : "Member"
        }));
        setTeams(processedTeams);
      }
    } catch (err) {
      console.error(`Failed to ${response} invitation:`, err);
    }
  };
  
  // Game-specific colors and icons (update to use team_type)
  const gameStyles = {
    "Free Fire": {
      bg: "bg-orange-100",
      text: "text-orange-800",
      icon: "🔥"
    },
    "PUBG Mobile": {
      bg: "bg-yellow-100",
      text: "text-yellow-800",
      icon: "🔫"
    },
    "Call of Duty": {
      bg: "bg-red-100",
      text: "text-red-800",
      icon: "🎖️"
    },
    "Valorant": {
      bg: "bg-purple-100",
      text: "text-purple-800",
      icon: "🎯"
    },
    "BGMI": {
      bg: "bg-blue-100",
      text: "text-blue-800",
      icon: "🎮"
    },
    "default": {
      bg: "bg-gray-100",
      text: "text-gray-800",
      icon: "🎲"
    }
  };
  
  // Get game style based on game name
  const getGameStyle = (game) => {
    return gameStyles[game] || gameStyles.default;
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };
  
  // Role badge styling
  const getRoleBadgeStyle = (role) => {
    if (role === "Leader" || role === "Owner") {
      return "bg-red-100 text-red-800";
    } else if (role === "Co-Leader") {
      return "bg-orange-100 text-orange-800";
    } else {
      return "bg-blue-100 text-blue-800";
    }
  };

  // Add a function to handle player search
  useEffect(() => {
    const handleSearch = async () => {
      // Don't search if term is less than 3 characters
      if (searchTerm.length < 3) {
        setSearchResults([]);
        setSearchError(null);
        return;
      }
      
      try {
        setIsSearching(true);
        setSearchError(null);
        
        // Use the searchPlayers function from TeamsApi
        const response = await fetch(`${API_BASE_URL}/playerapi/search/?username=${encodeURIComponent(searchTerm)}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem("access_token")}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`Search failed: ${response.status}`);
        }

        const data = await response.json();
        
        // Handle the search response format correctly
        let resultsArray = [];
        if (data.results && Array.isArray(data.results)) {
          // Format matches: { "search_term": "...", "count": X, "results": [...] }
          resultsArray = data.results;
        } else if (Array.isArray(data)) {
          // Format matches direct array: [...]
          resultsArray = data;
        } else {
          console.warn("Unexpected search result format:", data);
          resultsArray = [];
        }
        
        // console.log("Search results:", resultsArray);
        setSearchResults(resultsArray);
      } catch (error) {
        console.error("Error searching players:", error);
        setSearchError(error.message);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    };
    
    // Debounce search to avoid too many requests
    const timer = setTimeout(() => {
      if (searchTerm.length >= 3) {
        handleSearch();
      }
    }, 300); // Reduced to 300ms for faster response
    
    return () => clearTimeout(timer);
  }, [searchTerm]);
  
  // Add a function to handle sending invitations directly
  const invitePlayer = async (teamId, playerId) => {
    if (!teamId || !playerId) {
      setInviteError("Missing team ID or player ID");
      return;
    }
    
    try {
      setInviteError(null);
      setInviteSuccess(null);
      
      const response = await fetch(`${API_BASE_URL}/teamsapi/${teamId}/invite-player/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem("access_token")}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ player_id: playerId })
      });
      
      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(errorData || "Failed to send invitation");
      }
      
      const data = await response.json();
      
      // Show success message with player's email
      const invitedPlayer = searchResults.find(player => player.user_id === playerId);
      setInviteSuccess(`Invitation sent successfully to ${invitedPlayer?.email || 'player'}!`);
      
      // Keep the search results but mark this player as invited
      setSearchResults(prevResults => 
        Array.isArray(prevResults) ? prevResults.map(player => 
          player.user_id === playerId 
            ? {...player, invited: true} 
            : player
        ) : []
      );
      
    } catch (error) {
      console.error("Error inviting player:", error);
      setInviteError(`Failed to invite player: ${error.message}`);
    }
  };
  
  // Add this function to load team members
  const loadTeamMembers = async (teamId) => {
    try {
      setLoadingMembers(true);
      const response = await fetch(`${API_BASE_URL}/teamsapi/${teamId}/members/`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem("access_token")}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch team members: ${response.status}`);
      }

      const data = await response.json();
      setTeamMembers(data);
    } catch (error) {
      console.error("Error loading team members:", error);
    } finally {
      setLoadingMembers(false);
    }
  };

  // Add function to remove team member
  const removeTeamMember = async (teamId, playerId) => {
    try {
      setMemberRemovalError(null);
      
      const response = await fetch(`${API_BASE_URL}/teamsapi/${teamId}/remove-member/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem("access_token")}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ player_id: playerId })
      });

      // Handle error responses from the server
      if (!response.ok) {
        const errorData = await response.text();
        let errorMessage;
        
        try {
          // Try to parse the error as JSON
          const jsonError = JSON.parse(errorData);
          errorMessage = jsonError.error || jsonError.message || "Failed to remove team member";
        } catch (e) {
          // If not JSON, use the raw text
          errorMessage = errorData || "Failed to remove team member";
        }
        
        setMemberRemovalError(errorMessage);
        throw new Error(errorMessage);
      }

      // Update members list on success
      setTeamMembers(prev => prev.filter(member => member.user_id !== playerId));
      setRemoveConfirmation(null);
    } catch (error) {
      console.error("Error removing team member:", error);
      // Error is already set above, no need to set it again
    }
  };

  // Add a function to delete a team
  const deleteTeam = async (teamId) => {
    try {
      await fetch(`${API_BASE_URL}/teamsapi/${teamId}/?confirm=true`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem("access_token")}`,
          'Content-Type': 'application/json'
        }
      });

      setTeams(prevTeams => prevTeams.filter(team => team.id !== teamId));
      setDeleteConfirmation(null); // Close the modal after deletion
    } catch (error) {
      console.error("Error deleting team:", error);
      alert("Failed to delete the team. Please try again.");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
        <p className="font-bold">Error</p>
        <p>{error}</p>
      </div>
    );
  }

  // Check if user has any pending invitations
  const hasPendingInvitations = teamInvitations.length > 0;

  return (
    <div className="space-y-4">
      {/* Header with action buttons */}
      <div className="flex flex-wrap justify-between items-center gap-2 mb-2">
        <h2 className="text-xl font-bold text-gray-800">My Teams</h2>
        
        <div className="flex space-x-2">
          <Link 
            to="/player/teams/create" 
            className="px-3 py-1.5 bg-primary-600 text-white text-sm rounded hover:bg-primary-700 transition flex items-center"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            New Team
          </Link>
          
          <button 
            onClick={() => setShowInvitations(!showInvitations)}
            className={`px-3 py-1.5 text-sm rounded flex items-center ${
              hasPendingInvitations 
                ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            {hasPendingInvitations ? `${teamInvitations.length} Invitation${teamInvitations.length > 1 ? 's' : ''}` : 'Invitations'}
          </button>
        </div>
      </div>
      
      {/* Team Invitations Section - Only shown when toggled */}
      {showInvitations && (
        <div className="bg-white rounded-lg shadow-md overflow-hidden mb-4">
          <div className="px-4 py-3 bg-gray-50 border-b">
            <h3 className="font-medium text-gray-800">Team Invitations</h3>
          </div>
          
          {invitationsLoading ? (
            <div className="p-6 text-center">
              <div className="animate-spin inline-block w-6 h-6 border-2 border-current border-t-transparent text-primary-600 rounded-full"></div>
              <p className="mt-2 text-sm text-gray-500">Loading invitations...</p>
            </div>
          ) : teamInvitations.length > 0 ? (
            <div className="divide-y">
              {teamInvitations.map(invitation => (
                <div key={invitation.id} className="p-3">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                    <div>
                      <h4 className="font-medium text-gray-800">{invitation.name}</h4>
                      <p className="text-xs text-gray-500">From: {invitation.owner} • {formatDate(invitation.invitedAt)}</p>
                    </div>
                    <div className="flex gap-2 self-end sm:self-auto">
                      <button 
                        onClick={() => handleInvitationResponse(invitation.id, 'accept')}
                        className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition"
                      >
                        Accept
                      </button>
                      <button 
                        onClick={() => handleInvitationResponse(invitation.id, 'reject')}
                        className="px-3 py-1 bg-white border border-gray-300 text-gray-700 text-sm rounded hover:bg-gray-50 transition"
                      >
                        Decline
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-6 text-center text-gray-500">
              <p className="text-sm">You don't have any pending team invitations.</p>
            </div>
          )}
        </div>
      )}
      
      {/* Search and Invite Section - Enhanced for better UX */}
      {teams.some(team => team.role === "Leader" || team.role === "Owner") && (
        <div className="bg-white rounded-lg shadow-md overflow-hidden mb-4">
          <div className="p-4 border-b bg-gradient-to-r from-gray-50 to-blue-50">
            <h3 className="text-sm font-medium text-gray-700 mb-1">Invite Players to Your Team</h3>
            <p className="text-xs text-gray-500">Search by username or email to send invites instantly</p>
          </div>
          
          <div className="p-4">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              {/* Team selector with improved styling */}
              {teams.filter(team => team.role === "Leader" || team.role === "Owner").length > 0 && (
                <div className="w-full flex items-center">
                  <label className="text-sm text-gray-600 mr-2">Select Team:</label>
                  <select 
                    className="flex-grow text-sm border rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                    value={selectedTeamId || ""}
                    onChange={(e) => setSelectedTeamId(e.target.value)}
                  >
                    <option value="" disabled>Choose a team to manage</option>
                    {teams
                      .filter(team => team.role === "Leader" || team.role === "Owner")
                      .map(team => (
                        <option key={team.id} value={team.id}>{team.name}</option>
                      ))
                    }
                  </select>
                </div>
              )}
            </div>
            
            {/* Search box with improved UI */}
            <div className="relative mt-3">
              <div className="flex">
                <div className="relative flex-grow">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    placeholder="Search players by username or email (min 3 chars)"
                    className={`w-full pl-10 pr-12 py-2 border rounded-md focus:ring-primary-500 focus:border-primary-500 ${
                      searchTerm.length > 0 && searchTerm.length < 3 ? 'border-yellow-300 bg-yellow-50' : ''
                    }`}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    disabled={!selectedTeamId}
                  />
                  {isSearching && (
                    <div className="absolute right-3 top-2.5 animate-spin h-4 w-4 border-2 border-primary-500 rounded-full border-t-transparent"></div>
                  )}
                  {!isSearching && searchTerm.length > 0 && searchTerm.length < 3 && (
                    <div className="absolute right-3 top-2.5 text-yellow-500">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </div>
              </div>

              {/* Character minimum indicator */}
              {searchTerm.length > 0 && searchTerm.length < 3 && (
                <div className="mt-2 p-2 bg-yellow-50 text-yellow-700 text-xs rounded-md border border-yellow-200 flex items-center">
                  <svg className="h-4 w-4 text-yellow-500 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  Please enter at least 3 characters to search
                  <span className="ml-1 font-medium">{searchTerm.length}/3</span>
                </div>
              )}
              
              {/* Success/Error messages with improved styling */}
              {inviteSuccess && (
                <div className="mt-2 p-3 bg-green-50 text-green-700 text-sm rounded-md border border-green-200 flex items-center">
                  <svg className="h-5 w-5 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {inviteSuccess}
                </div>
              )}
              
              {inviteError && (
                <div className="mt-2 p-3 bg-red-50 text-red-700 text-sm rounded-md border border-red-200 flex items-center">
                  <svg className="h-5 w-5 text-red-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {inviteError}
                </div>
              )}
              
              {/* Search results with improved styling and feedback */}
              {searchTerm.length >= 3 && searchResults.length > 0 && (
                <div className="mt-3 border rounded-md overflow-hidden bg-gray-50">
                  <div className="p-2 bg-gray-100 border-b">
                    <p className="text-xs text-gray-500">Found {searchResults.length} players matching "{searchTerm}"</p>
                  </div>
                  <ul className="max-h-60 overflow-y-auto divide-y divide-gray-200">
                    {searchResults.map(player => (
                      <li key={player.user_id} className="p-3 flex justify-between items-center hover:bg-gray-100">
                        <div>
                          <p className="text-sm font-medium">{player.email}</p>
                        </div>
                        {player.invited ? (
                          <span className="text-xs bg-green-100 text-green-800 py-1 px-2 rounded-full">
                            Invited
                          </span>
                        ) : (
                          <button
                            onClick={() => invitePlayer(selectedTeamId, player.user_id)}
                            className="ml-2 px-3 py-1.5 bg-primary-600 text-white text-xs rounded hover:bg-primary-700 transition flex items-center"
                            disabled={!selectedTeamId}
                          >
                            <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            Invite
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {searchTerm.length >= 3 && searchResults.length === 0 && !isSearching && !searchError && (
                <div className="mt-3 p-4 text-center text-gray-500 bg-gray-50 rounded-md border border-gray-200">
                  <svg className="h-10 w-10 mx-auto text-gray-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  <p className="text-sm">No players found matching "{searchTerm}"</p>
                  <p className="text-xs mt-1">Try a different search term or check the spelling</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Teams Grid - Redesigned to be more compact */}
      {teams.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {teams.map(team => {
            const gameStyle = getGameStyle(team.game);
            return (
              <div key={team.id} className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                <div className="p-3">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h3 className="font-bold text-gray-800">{team.name}</h3>
                      <div className="flex items-center text-xs text-gray-500 mt-0.5">
                        <span className="mr-1">{gameStyle.icon}</span>
                        <span className={gameStyle.text}>{team.game}</span>
                        <span className="mx-1">•</span>
                        <span>{team.members} members</span>
                      </div>
                    </div>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeStyle(team.role)}`}>
                      {team.role}
                    </span>
                  </div>
                  {team.bio && (
                    <p className="text-xs text-gray-600 mb-3 line-clamp-1">{team.bio}</p>
                  )}
                  <div className="flex justify-end space-x-2 mt-2">
                    {team.role === "Leader" || team.role === "Owner" ? (
                      <>
                        <button 
                          onClick={() => {
                            setSelectedTeamForManage(team);
                            loadTeamMembers(team.id);
                          }}
                          className="px-2 py-1 bg-primary-600 text-white text-xs rounded hover:bg-primary-700 transition"
                        >
                          Manage
                        </button>
                        <button 
                          onClick={() => setDeleteConfirmation(team.id)} // Open the modal
                          className="px-2 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 transition"
                        >
                          Delete
                        </button>
                      </>
                    ) : null}
                    <button 
                      onClick={() => {
                        setSelectedTeamForDetails(team);
                        loadTeamMembers(team.id);
                      }}
                      className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded hover:bg-gray-200 transition"
                    >
                      View Details
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
          
          {/* Create Team Card - Simplified */}
          <div className="bg-white border border-dashed border-gray-300 rounded-lg hover:bg-gray-50 transition">
            <Link 
              to="/player/teams/create"
              className="h-full p-3 flex flex-col items-center justify-center text-center"
            >
              <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 mb-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              </div>
              <p className="text-sm font-medium text-gray-700">Create New Team</p>
              <p className="text-xs text-gray-500 mt-1">Start your own esports team</p>
            </Link>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md p-6 text-center">
          <div className="text-4xl mb-3">👥</div>
          <h3 className="text-lg font-bold text-gray-800 mb-2">No Teams Yet</h3>
          <p className="text-gray-600 text-sm mb-4">You're not part of any teams yet. Create a team or join an existing one.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link 
              to="/player/teams/create" 
              className="py-2 px-4 bg-primary-600 text-white text-center rounded-lg hover:bg-primary-700 shadow-sm transition text-sm"
            >
              Create Team
            </Link>
            <Link 
              to="/player/teams/join" 
              className="py-2 px-4 bg-gray-600 text-white text-center rounded-lg hover:bg-gray-700 shadow-sm transition text-sm"
            >
              Browse Teams
            </Link>
          </div>
        </div>
      )}
      
      {/* Team Details Modal Component - Enhanced styling */}
      {selectedTeamForDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-4 border-b flex justify-between items-center bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-t-lg">
              <h3 className="text-lg font-bold">{selectedTeamForDetails.name}</h3>
              <button 
                onClick={() => setSelectedTeamForDetails(null)}
                className="text-white hover:text-gray-200"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4">
              {selectedTeamForDetails.bio && (
                <div className="mb-4 bg-blue-50 p-3 rounded-md border border-blue-100">
                  <h4 className="text-sm font-medium text-blue-700 mb-1">About</h4>
                  <p className="text-sm text-gray-600">{selectedTeamForDetails.bio}</p>
                </div>
              )}
              
              <div className="mb-4 bg-gray-50 p-3 rounded-md border border-gray-200">
                <div className="flex items-center mb-1">
                  {getGameStyle(selectedTeamForDetails.game).icon && (
                    <span className="mr-2 text-xl">{getGameStyle(selectedTeamForDetails.game).icon}</span>
                  )}
                  <h4 className="text-sm font-medium text-gray-700">Game</h4>
                </div>
                <p className={`text-sm ${getGameStyle(selectedTeamForDetails.game).text}`}>{selectedTeamForDetails.game}</p>
              </div>
              
              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <h4 className="text-sm font-medium text-gray-700">Team Members</h4>
                  <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-0.5 rounded-full">
                    {teamMembers.length} members
                  </span>
                </div>
                {loadingMembers ? (
                  <div className="text-center py-4">
                    <div className="inline-block animate-spin h-6 w-6 border-2 border-primary-600 border-t-transparent rounded-full"></div>
                  </div>
                ) : teamMembers.length > 0 ? (
                  <ul className="divide-y divide-gray-200 border rounded-md overflow-hidden">
                    {teamMembers.map((member, index) => (
                      <li 
                        key={member.user_id} 
                        className={`p-3 flex justify-between items-center ${index % 2 === 0 ? 'bg-white' : 'bg-blue-50'} hover:bg-blue-100 transition-colors`}
                      >
                        <div className="flex items-center">
                          <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center mr-3 font-medium">
                            {member.email.charAt(0).toUpperCase()}
                          </div>
                          <div className="text-sm">
                            <p className="font-medium text-gray-700">{member.email}</p>
                            {selectedTeamForDetails.role === "Leader" && 
                              member.email === JSON.parse(localStorage.getItem("user_info") || "{}").email && (
                              <span className="text-xs text-yellow-600">Team Owner</span>
                            )}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-center p-6 bg-gray-50 rounded-md border border-gray-200">
                    <svg className="h-10 w-10 text-gray-400 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                    <p className="text-sm text-gray-500">No members found</p>
                  </div>
                )}
              </div>
            </div>
            <div className="bg-gray-50 px-4 py-3 border-t flex justify-end">
              <button
                onClick={() => setSelectedTeamForDetails(null)}
                className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 transition shadow-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Team Management Modal Component */}
      {selectedTeamForManage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-4 border-b flex justify-between items-center bg-gradient-to-r from-primary-700 to-primary-800 text-white rounded-t-lg">
              <h3 className="text-lg font-bold">Manage Team: {selectedTeamForManage.name}</h3>
              <button 
                onClick={() => {
                  setSelectedTeamForManage(null);
                  setRemoveConfirmation(null);
                  setMemberRemovalError(null);
                }}
                className="text-white hover:text-gray-200"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Error message display */}
            {memberRemovalError && (
              <div className="p-3 m-4 bg-red-50 text-red-700 border border-red-200 rounded-md">
                <div className="flex items-center">
                  <svg className="h-5 w-5 text-red-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>{memberRemovalError}</span>
                </div>
              </div>
            )}
            
            <div className="p-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Team Members ({teamMembers.length})</h4>
              {loadingMembers ? (
                <div className="text-center py-4">
                  <div className="inline-block animate-spin h-6 w-6 border-2 border-primary-600 border-t-transparent rounded-full"></div>
                </div>
              ) : teamMembers.length > 0 ? (
                <ul className="divide-y divide-gray-200 border rounded-md">
                  {teamMembers.map(member => (
                    <li key={member.user_id} className="p-3 flex justify-between items-center hover:bg-gray-50">
                      <div className="text-sm">
                        <p className="font-medium text-gray-700">{member.email}</p>
                      </div>
                      {removeConfirmation === member.user_id ? (
                        // Mobile-friendly confirmation dialog
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => removeTeamMember(selectedTeamForManage.id, member.user_id)}
                            className="p-1.5 bg-red-600 text-white text-xs rounded hover:bg-red-700 transition flex items-center"
                            aria-label="Confirm removal"
                          >
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </button>
                          <button
                            onClick={() => setRemoveConfirmation(null)}
                            className="p-1.5 bg-gray-200 text-gray-700 text-xs rounded hover:bg-gray-300 transition flex items-center"
                            aria-label="Cancel removal"
                          >
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ) : (
                        // Replace text with icon for the remove button
                        <button
                          onClick={() => setRemoveConfirmation(member.user_id)}
                          className="p-1.5 bg-red-100 text-red-700 text-xs rounded hover:bg-red-200 transition flex items-center"
                          aria-label="Remove member"
                        >
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-gray-500 text-center py-2">No members found</p>
              )}
            </div>
            <div className="bg-gray-50 px-4 py-3 border-t flex justify-end">
              <button
                onClick={() => {
                  setSelectedTeamForManage(null);
                  setRemoveConfirmation(null);
                  setMemberRemovalError(null);
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Delete Confirmation Modal */}
      {deleteConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-4 border-b flex justify-between items-center bg-red-600 text-white rounded-t-lg">
              <h3 className="text-lg font-bold">Confirm Deletion</h3>
              <button 
                onClick={() => setDeleteConfirmation(null)} // Close the modal
                className="text-white hover:text-gray-200"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4">
              <p className="text-sm text-gray-700">Are you sure you want to delete this team? This action cannot be undone.</p>
            </div>
            <div className="bg-gray-50 px-4 py-3 border-t flex justify-end space-x-2">
              <button
                onClick={() => setDeleteConfirmation(null)} // Cancel deletion
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteTeam(deleteConfirmation)} // Confirm deletion
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlayerTeams;
